import type { CatalogEntry, CatalogFile, CatalogImportResult } from '../types/catalog';
import type { DriveProfile, Vendor } from '../types/drive';
import { CatalogImporter } from './CatalogImporter';
import { getProfile, setDynamicProfile } from '../config/profiles';
import { createProfileFromCatalog } from './profileFactory';
import { applyCatalogFilter } from '../utils/catalogFilter';
import type { CatalogFilter } from '../utils/catalogFilter';
import {
  loadCatalogs,
  loadPendingOps,
  loadRemoteMap,
  saveCatalogs,
  savePendingOps,
  saveRemoteMap,
  type PendingOp,
} from './CatalogStorage';
import { syncAddToRemote, syncPending, syncRemoveFromRemote, type SyncBackend } from './CatalogCloudSync';

export type { CatalogFilter } from '../utils/catalogFilter';

export interface ImportOutcome {
  success: boolean;
  catalog?: CatalogFile;
  errors: string[];
  warnings: string[];
}

export class CatalogService implements SyncBackend {
  private catalogs: Map<string, CatalogFile> = new Map();
  private allModels: CatalogEntry[] = [];
  private listeners: Array<() => void> = [];
  private remoteIdMap: Map<string, string> = new Map();
  private pendingOps: PendingOp[] = [];
  private onlineSyncInitialized = false;

  constructor() {
    const stored = loadCatalogs();
    for (const [key, catalog] of stored) {
      this.catalogs.set(key, catalog);
    }
    this.rebuildIndex();
    this.deduplicateExisting();
    for (const [key, id] of loadRemoteMap()) {
      this.remoteIdMap.set(key, id);
    }
    this.pendingOps = loadPendingOps();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter(l => l !== fn);
    };
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
  }

  addCatalog(catalog: CatalogFile, key?: string): { added: number; skipped: CatalogEntry[] } {
    const known = new Set(this.allModels.map(m => m.modelCode));
    const fresh: CatalogEntry[] = [];
    const skipped: CatalogEntry[] = [];

    for (const model of catalog.models) {
      if (known.has(model.modelCode)) {
        skipped.push(model);
      } else {
        known.add(model.modelCode);
        fresh.push(model);
      }
    }

    if (fresh.length === 0) {
      return { added: 0, skipped };
    }

    const freshCatalog: CatalogFile = { ...catalog, models: fresh };
    const catalogKey =
      key ??
      `${catalog.vendor}-${catalog.family}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    this.catalogs.set(catalogKey, freshCatalog);
    this.rebuildIndex();
    this.saveToStorage();
    this.notify();
    this.syncAddToRemote(catalogKey, freshCatalog, key !== undefined);

    return { added: fresh.length, skipped };
  }

  removeCatalog(key: string): boolean {
    const removed = this.catalogs.delete(key);
    if (removed) {
      this.rebuildIndex();
      this.saveToStorage();
      this.notify();
      this.syncRemoveFromRemote(key);
    }
    return removed;
  }

  getCatalogs(): Array<{ key: string; catalog: CatalogFile }> {
    return Array.from(this.catalogs.entries()).map(([key, catalog]) => ({ key, catalog }));
  }

  getModelCount(): number {
    return this.allModels.length;
  }

  getAllModels(): CatalogEntry[] {
    return [...this.allModels];
  }

  getByVendor(vendor: Vendor): CatalogEntry[] {
    return this.allModels.filter(m => m.vendor === vendor);
  }

  getByFamily(family: string): CatalogEntry[] {
    return this.allModels.filter(m => m.family.toLowerCase() === family.toLowerCase());
  }

  getModel(modelCode: string): CatalogEntry | undefined {
    return this.allModels.find(m => m.modelCode === modelCode);
  }

  getVendors(): Vendor[] {
    const vendors = new Set<Vendor>();
    for (const m of this.allModels) vendors.add(m.vendor);
    return Array.from(vendors).sort();
  }

  getFamilies(vendor?: Vendor): string[] {
    const families = new Set<string>();
    for (const m of this.allModels) {
      if (vendor && m.vendor !== vendor) continue;
      families.add(m.family);
    }
    return Array.from(families).sort();
  }

  search(query: string): CatalogEntry[] {
    const q = query.toLowerCase();
    return this.allModels.filter(m =>
      m.modelCode.toLowerCase().includes(q) ||
      m.name.toLowerCase().includes(q) ||
      m.family.toLowerCase().includes(q) ||
      m.vendor.toLowerCase().includes(q)
    );
  }

  filter(filter: CatalogFilter): CatalogEntry[] {
    return applyCatalogFilter(this.allModels, filter);
  }

  setRemoteId(localKey: string, remoteId: string): void {
    this.remoteIdMap.set(localKey, remoteId);
    this.saveRemoteMap();
  }

  getRemoteId(localKey: string): string | undefined {
    return this.remoteIdMap.get(localKey);
  }

  deleteRemoteId(localKey: string): void {
    this.remoteIdMap.delete(localKey);
    this.saveRemoteMap();
  }

  generateProfile(entry: CatalogEntry): DriveProfile {
    return createProfileFromCatalog(entry);
  }

  importJSON(jsonText: string, fileName: string): ImportOutcome {
    return this.applyImport(CatalogImporter.importJSON(jsonText, fileName));
  }

  importCSV(csvText: string, fileName: string): ImportOutcome {
    return this.applyImport(CatalogImporter.importCSV(csvText, fileName));
  }

  private applyImport(
    result: Pick<CatalogImportResult, 'success' | 'catalogFile' | 'errors' | 'warnings'>,
  ): ImportOutcome {
    if (!result.success || !result.catalogFile) {
      return { success: false, errors: result.errors, warnings: result.warnings };
    }

    const { added, skipped } = this.addCatalog(result.catalogFile);
    const warnings = [...result.warnings];

    for (const model of skipped) {
      warnings.push(`Modelo ${model.modelCode} já cadastrado, ignorado`);
    }

    if (added === 0) {
      return {
        success: false,
        errors: [...result.errors, 'Nenhum modelo novo: todos os modelos já estão cadastrados'],
        warnings,
      };
    }

    return { success: true, catalog: result.catalogFile, errors: result.errors, warnings };
  }

  exportAllToJSON(): string {
    const catalogs = Array.from(this.catalogs.entries()).map(([key, catalog]) => ({
      key,
      catalog,
    }));
    return JSON.stringify({ exportedAt: new Date().toISOString(), catalogs }, null, 2);
  }

  initOnlineSync(): void {
    if (this.onlineSyncInitialized) return;
    this.onlineSyncInitialized = true;

    window.addEventListener('online', () => {
      this.syncPending();
    });
  }

  async syncPending(): Promise<void> {
    return syncPending(this);
  }

  getPendingOpCount(): number {
    return this.pendingOps.length;
  }

  getPendingOps(): PendingOp[] {
    return this.pendingOps;
  }

  setPendingOps(ops: PendingOp[]): void {
    this.pendingOps = ops;
  }

  private async syncAddToRemote(localKey: string, catalog: CatalogFile, isFromRemote: boolean): Promise<void> {
    return syncAddToRemote(this, localKey, catalog, isFromRemote);
  }

  private async syncRemoveFromRemote(localKey: string): Promise<void> {
    return syncRemoveFromRemote(this, localKey);
  }

  addPendingOp(op: Omit<PendingOp, 'id' | 'timestamp' | 'retries'>): void {
    const fullOp: PendingOp = {
      ...op,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: Date.now(),
      retries: 0,
    };
    this.pendingOps.push(fullOp);
    this.savePendingOps();
    this.notify();
  }

  private rebuildIndex(): void {
    this.allModels = [];
    for (const catalog of this.catalogs.values()) {
      for (const model of catalog.models) {
        this.allModels.push(model);
      }
    }
  }

  /**
   * Remove duplicatas legadas por modelCode entre todos os catálogos locais,
   * mantendo a primeira ocorrência. Catálogos que ficarem vazios são removidos.
   * Idempotente; roda no carregamento do serviço.
   */
  deduplicateExisting(): void {
    const seen = new Set<string>();
    const empties: string[] = [];
    let changed = false;

    for (const [key, catalog] of this.catalogs) {
      const fresh: CatalogEntry[] = [];
      for (const model of catalog.models) {
        if (seen.has(model.modelCode)) continue;
        seen.add(model.modelCode);
        fresh.push(model);
      }
      if (fresh.length === 0) {
        empties.push(key);
        changed = true;
      } else if (fresh.length !== catalog.models.length) {
        this.catalogs.set(key, { ...catalog, models: fresh });
        changed = true;
      }
    }

    for (const key of empties) {
      this.catalogs.delete(key);
    }

    if (changed) {
      this.rebuildIndex();
      this.saveToStorage();
      this.notify();
    }
  }

  private saveToStorage(): void {
    saveCatalogs(Array.from(this.catalogs.entries()));
  }

  saveRemoteMap(): void {
    saveRemoteMap(Array.from(this.remoteIdMap.entries()));
  }

  savePendingOps(): void {
    savePendingOps(this.pendingOps);
  }

  hasModels(): boolean {
    return this.allModels.length > 0;
  }

  /**
   * Re-registra perfis dinâmicos gerados a partir dos catálogos importados no
   * mapa global de perfis (DRIVE_PROFILES). Necessário após um reload, pois
   * o snapshot do estado pode referenciar um modelo de catálogo.
   */
  registerDynamicProfiles(): void {
    for (const entry of this.allModels) {
      const profile = this.generateProfile(entry);
      const existing = getProfile(profile.id);
      if (!existing) {
        setDynamicProfile(profile.id, profile);
      }
    }
  }
}